/* Trova Stazioni: init mappa e markers (geolocalizzazione isolata e persistente) */
(function () {
  "use strict";

  function setCookie(name, value, days = 7) {
    const d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/`;
  }

  function getCookie(name) {
    const match = document.cookie.match(
      new RegExp("(^| )" + name + "=([^;]+)"),
    );
    return match ? decodeURIComponent(match[2]) : null;
  }

  const cfg = window.VCStationsConfig || {};
  const MAP_CONTAINER_SEL = "#vc-stations-map";

  const LIST_ITEM_SEL = ".vc-station-item";
  const GEO_RADIUS_KM = 15;
  const ST_DETAIL_SEL = '[data-role="station-detail"]';
  const ST_TITLE_SEL = '[data-role="station-title"]';
  const ST_BODY_SEL = '[data-role="station-body"]';
  const ST_FOOTER_SEL = '[data-role="station-footer"]';
  const ST_CLOSE_BTN_SEL = '[data-action="close-station-detail"]';

  let map;
  let infoWindow;
  let markersById = new Map();
  let markerClusterer = null;

  // Stato per autocomplete/search
  let lastPlaceLoc = null;
  let lastPlaceTypes = [];
  // Testo del campo indirizzo al momento della selezione: se l'utente modifica
  // il testo dopo aver scelto un suggerimento, lastPlaceLoc non è più valido.
  let lastPlaceAddress = null;
  let autocompleteInstance = null;
  // Assegnata in init(): esegue la ricerca indirizzo corrente (testo o posizione).
  let runAddressSearch = null;

  /* -------------------------------------------------- */
  function inScope() {
    return document.querySelector(MAP_CONTAINER_SEL) !== null;
  }

  /* -------------------------------------------------- */
  function loadGMaps(apiKey, language) {
    return new Promise((resolve, reject) => {
      if (window.google?.maps) return resolve(window.google.maps);
      if (!apiKey) return reject(new Error("Missing API key"));

      const cb = "VCStationsInitGMaps_" + Math.random().toString(36).slice(2);
      window[cb] = () => {
        delete window[cb];
        resolve(window.google.maps);
      };

      const s = document.createElement("script");
      const params = new URLSearchParams({
        key: apiKey,
        callback: cb,
        libraries: "marker,places",
        loading: "async",
        v: "weekly",
      });

      if (language) params.set("language", language);
      s.src = "https://maps.googleapis.com/maps/api/js?" + params.toString();
      s.async = true;
      s.defer = true;
      s.onerror = () => reject(new Error("Maps load failed"));
      document.head.appendChild(s);
    });
  }

  /* -------------------------------------------------- */
  function collectStations(scope = document) {
    return Array.from(scope.querySelectorAll(LIST_ITEM_SEL))
      .map((el) => {
        const lat = parseFloat(el.dataset.lat);
        const lng = parseFloat(el.dataset.lng);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

        return {
          id: el.dataset.id,
          el,
          lat,
          lng,
          title: el.dataset.title || "",
          address: el.dataset.address || "",
          brand: el.dataset.brand || "",
          brandLabel: el.dataset.brandLabel || "",
          brandImg: el.dataset.brandImg || "",
          phone: el.dataset.phone || "",
          permalink: el.dataset.permalink || "",
          terms: el.dataset.termsJson ? JSON.parse(el.dataset.termsJson) : {},
        };
      })
      .filter(Boolean);
  }

  /* -------------------------------------------------- */
  function resetPlacesState() {
    lastPlaceLoc = null;
    lastPlaceTypes = [];
    lastPlaceAddress = null;
  }

  /* -------------------------------------------------- */
  function initAutocomplete() {
    const input = document.getElementById("vc-stations-address-query");
    if (!input || !google.maps.places) return;

    if (autocompleteInstance) {
      google.maps.event.clearInstanceListeners(autocompleteInstance);
      autocompleteInstance = null;
    }

    autocompleteInstance = new google.maps.places.Autocomplete(input, {
      fields: ["geometry", "types"],
      // types: ["(regions)"],
      componentRestrictions: { country: "it" },
    });

    autocompleteInstance.addListener("place_changed", () => {
      const place = autocompleteInstance.getPlace();
      lastPlaceTypes = place?.types || [];
      lastPlaceLoc = place?.geometry?.location
        ? {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          }
        : null;
      lastPlaceAddress = lastPlaceLoc ? input.value.trim() : null;

      // Avvia subito la ricerca alla selezione di un suggerimento (click o
      // tastiera), senza attendere un secondo Invio o il click su "Cerca".
      if (lastPlaceLoc && runAddressSearch) runAddressSearch();
    });

    input.addEventListener("input", resetPlacesState);
  }

  /* -------------------------------------------------- */
  function rebuildMarkersAndFit(items, centerOverride = null) {
    if (!infoWindow) infoWindow = new google.maps.InfoWindow();
    const hasAdvanced = !!google.maps.marker?.AdvancedMarkerElement;

    // clear previous clusterer and markers
    if (markerClusterer && markerClusterer.clearMarkers) {
      markerClusterer.clearMarkers();
      markerClusterer = null;
    }

    markersById.forEach((m) => (hasAdvanced ? (m.map = null) : m.setMap(null)));
    markersById.clear();

    if (!items.length) return;

    const bounds = new google.maps.LatLngBounds();
    const markers = [];

    const iconForBrand = (brand) => {
      const url = cfg.brandIcons?.[brand];
      if (!url) return null;
      return hasAdvanced
        ? url
        : {
            url,
            scaledSize: new google.maps.Size(32, 42),
            anchor: new google.maps.Point(16, 42),
          };
    };

    items.forEach((st) => {
      const position = { lat: st.lat, lng: st.lng };
      const icon = iconForBrand(st.brand);

      let marker;
      if (hasAdvanced) {
        if (icon) {
          const img = document.createElement("img");
          img.src = icon;
          img.alt = st.title || "";
          img.width = 32;
          img.height = 42;
          marker = new google.maps.marker.AdvancedMarkerElement({
            map,
            position,
            title: st.title,
            content: img,
          });
        } else {
          marker = new google.maps.marker.AdvancedMarkerElement({
            map,
            position,
            title: st.title,
          });
        }
      } else {
        marker = new google.maps.Marker({
          map,
          position,
          title: st.title,
          icon: icon || undefined,
        });
      }

      markersById.set(st.id, marker);
      markers.push(marker);
      bounds.extend(position);

      marker.addListener("click", () => {
        highlightItem(st.el);
        // openInfo(marker, st);
        scrollItemIntoView(st.el);
        openStationDetail(st);
      });

      const openStationItem = () => {
        map.panTo(position);
        map.setZoom(Math.max(map.getZoom(), 17));
        highlightItem(st.el);
        openStationDetail(st);
        if (window.innerWidth <= 767) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      };

      st.el.addEventListener("click", (e) => {
        if (!e.target.closest("a")) openStationItem();
      });

      st.el.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openStationItem();
        }
      });
    });

    // clustering (robusto con UMD window.markerClusterer) + renderer brand colors
    try {
      const ns =
        window.markerClusterer ||
        window.MarkerClusterer ||
        google.maps.marker?.MarkerClusterer ||
        null;
      const MCClass = ns && (ns.MarkerClusterer || ns);
      if (MCClass && typeof MCClass === "function" && markers.length) {
        const hasAdvanced = !!google.maps.marker?.AdvancedMarkerElement;
        const renderer = {
          render: ({ count, position }) => {
            if (hasAdvanced) {
              const div = document.createElement("div");
              div.style.width = "40px";
              div.style.height = "40px";
              div.style.borderRadius = "50%";
              div.style.display = "grid";
              div.style.placeItems = "center";
              div.style.background = "#1D1D1F"; // fill brand (inverted)
              div.style.border = "2px solid #1D1D1F"; // border brand (inverted)
              div.style.color = "#fff";
              div.style.fontWeight = "700";
              div.style.fontSize = "12px";
              div.textContent = String(count);
              return new google.maps.marker.AdvancedMarkerElement({
                map,
                position,
                content: div,
                zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
              });
            } else {
              return new google.maps.Marker({
                position,
                label: {
                  text: String(count),
                  color: "#fff",
                  fontSize: "12px",
                  fontWeight: "700",
                },
                icon: {
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 16,
                  fillColor: "#c22d21",
                  fillOpacity: 0.95,
                  strokeColor: "#06559A",
                  strokeWeight: 2,
                },
                zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
              });
            }
          },
        };

        // maxZoom basso: oltre questo livello i marker vicini non vengono più
        // raggruppati, restano sempre separati (usato insieme allo zoom "close"
        // impostato al click su una stazione).
        const algorithm = ns.SuperClusterAlgorithm
          ? new ns.SuperClusterAlgorithm({ radius: 60, maxZoom: 15 })
          : undefined;

        markerClusterer = new MCClass({ map, renderer, algorithm });
        if (typeof markerClusterer.addMarkers === "function") {
          markerClusterer.addMarkers(markers);
        } else if (typeof markerClusterer.addMarker === "function") {
          markers.forEach((m) => markerClusterer.addMarker(m));
        }
      }
    } catch (e) {
      // ignore clustering errors if library missing
    }

    if (centerOverride) {
      map.setCenter(centerOverride);
      map.setZoom(12);
    } else if (items.length > 1) {
      map.fitBounds(bounds, { top: 32, right: 32, bottom: 32, left: 32 });
    } else {
      map.setCenter(bounds.getCenter());
      map.setZoom(14);
    }
  }

  /* -------------------------------------------------- */
  function openInfo(marker, st) {
    if (!infoWindow) infoWindow = new google.maps.InfoWindow();

    infoWindow.setContent(
      [
        st.title &&
          `<div class="vc-infowindow__title">${escapeHtml(st.title)}</div>`,
        st.address &&
          `<div class="vc-infowindow__address">${escapeHtml(st.address)}</div>`,
      ]
        .filter(Boolean)
        .join(""),
    );
    infoWindow.open({ map, anchor: marker });
  }

  /* -------------------------------------------------- */
  // Risolve lat/lng per il testo indirizzo corrente. Se non corrisponde a un
  // suggerimento selezionato, geocodifica lato client: la chiave Google Maps
  // ha restrizioni referer e il geocoding server-side (PHP) viene rifiutato
  // da Google con "REQUEST_DENIED", lasciando la ricerca non filtrata.
  async function resolveQueryLocation(q) {
    if (lastPlaceLoc && lastPlaceAddress === q) {
      return { loc: lastPlaceLoc, types: lastPlaceTypes };
    }
    try {
      const geocoder = new google.maps.Geocoder();
      const { results } = await geocoder.geocode({
        address: q,
        componentRestrictions: { country: "it" },
      });
      const first = results?.[0];
      if (first?.geometry?.location) {
        return {
          loc: {
            lat: first.geometry.location.lat(),
            lng: first.geometry.location.lng(),
          },
          types: first.types || [],
        };
      }
    } catch (err) {
      console.warn("Geocoding lato client fallito per:", q, err);
    }
    return { loc: null, types: [] };
  }

  /* -------------------------------------------------- */
  async function fetchStations(params) {
    // Costruzione body che preserva gli array come chiavi ripetute (es. brand[])
    const body = new URLSearchParams();
    body.set("action", "vc_filter_stazioni");
    body.set("nonce", cfg.nonce || "");

    Object.entries(params || {}).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        v.forEach((val) => {
          if (val !== undefined && val !== null && String(val).trim() !== "") {
            body.append(k.endsWith("[]") ? k : `${k}[]`, val);
          }
        });
      } else if (v !== undefined && v !== null && String(v).trim() !== "") {
        body.append(k, v);
      }
    });

    const res = await fetch(cfg.ajaxUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      },
      body: body.toString(),
    });
    const json = await res.json();
    if (!json.success) throw new Error("AJAX error");
    return json.data;
  }

  /* -------------------------------------------------- */
  function attachGeolocateButton(listWrapper) {
    const geoBtn = document.querySelector(".vc-stations-list__geolocate");
    if (!geoBtn || !navigator.geolocation) return;

    geoBtn.addEventListener("click", () => {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const me = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            lastPlaceLoc = me; // aggiorna la variabile globale
            setCookie("vcUserLoc", JSON.stringify(me), 7); // salva nel cookie

            // chiudi infoWindow e rimuovi markers senza toccare listWrapper
            if (infoWindow) infoWindow.close();
            markersById.forEach((m) => {
              if (m.setMap) m.setMap(null);
              if (m.map) m.map = null;
            });
            markersById.clear();

            // fetch stazioni vicine alla posizione corrente
            const data = await fetchStations({
              mode: "nearby",
              lat: me.lat,
              lng: me.lng,
              radius: GEO_RADIUS_KM,
            });

            // aggiorna la lista e ricostruisci i marker
            if (listWrapper) listWrapper.innerHTML = data.html;
            const stations = collectStations(listWrapper);
            rebuildMarkersAndFit(stations, me);
            initAutocomplete();
          } catch (err) {
            console.warn("Geolocate fallita", err);
          }
        },
        (err) => console.warn("Geolocalizzazione negata o fallita", err),
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    });
  }

  /* -------------------------------------------------- */
  function hasAdvancedMarker(marker) {
    return (
      !!google.maps.marker?.AdvancedMarkerElement &&
      marker instanceof google.maps.marker.AdvancedMarkerElement
    );
  }

  /* -------------------------------------------------- */
  function init() {
    if (!inScope()) return;

    if (!cfg.gmaps?.apiKey) {
      console.warn(
        "VCStationsConfig.gmaps.apiKey mancante: configura la Google Maps API key nelle Opzioni del tema.",
      );
      return;
    }
    loadGMaps(cfg.gmaps?.apiKey, cfg.gmaps?.language).then(() => {
      const listWrapper =
        document.getElementById("vc-stations-list") ||
        document.getElementById("vc-stations-list-inner");

      map = new google.maps.Map(document.querySelector(MAP_CONTAINER_SEL), {
        center: { lat: 41.8719, lng: 12.5674 },
        zoom: 5,
        mapId: cfg.gmaps?.mapId,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        mapTypeControl: false,
        streetViewControl: false,
        zoomControl: true,
        fullscreenControl: false,
        gestureHandling: "greedy",
        zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_BOTTOM, // posizione dei pulsanti
        },
      });
      infoWindow = new google.maps.InfoWindow();

      rebuildMarkersAndFit(collectStations(listWrapper));
      initAutocomplete();
      attachGeolocateButton(listWrapper);
      initStationDetailPanel();

      listWrapper.addEventListener("click", async (e) => {
        const btn = e.target.closest('[data-action="reset-search"]');
        if (!btn) return;
        const filtersForm = document.getElementById("vc-stations-filters");
        if (filtersForm) {
          filtersForm.querySelectorAll('input[type="checkbox"]').forEach((i) => { i.checked = false; });
          const addressInput = document.getElementById("vc-stations-address-query");
          if (addressInput) addressInput.value = "";
        }
        const data = await fetchStations({ mode: "all", fallback: 1 });
        if (listWrapper) listWrapper.innerHTML = data.html;
        const stations = collectStations(listWrapper);
        rebuildMarkersAndFit(stations, data.center || null);
        initAutocomplete();
      });

      /* ---------------- SEARCH FORM ---------------- */
      async function performAddressSearch() {
        const input = document.getElementById("vc-stations-address-query");
        const q = input?.value.trim();
        if (!q) return;

        const { loc, types } = await resolveQueryLocation(q);

        const data = loc
          ? await fetchStations({
              mode: "nearby",
              lat: loc.lat,
              lng: loc.lng,
              radius: types.includes("locality") ? 15 : 5,
              fallback: 1,
              q,
            })
          : await fetchStations({ mode: "all", q, fallback: 1 });

        if (listWrapper) listWrapper.innerHTML = data.html;

        // riattacca listener e markers
        const stations = collectStations(listWrapper);
        rebuildMarkersAndFit(stations, data.center || null);
        initAutocomplete();
      }
      runAddressSearch = performAddressSearch;

      // Consente l'invio della ricerca anche premendo Invio nel campo indirizzo.
      // Registrato in fase di cattura per intercettare l'evento PRIMA del
      // listener interno di Google Places Autocomplete.
      document.addEventListener(
        "keydown",
        (e) => {
          if (e.key !== "Enter") return;
          const input = e.target.closest("#vc-stations-address-query");
          if (!input) return;

          // Se un suggerimento è evidenziato con le frecce, lascia che Google
          // lo selezioni normalmente: la ricerca partirà da "place_changed".
          const pac = document.querySelector(".pac-container");
          const pacOpen =
            pac &&
            window.getComputedStyle(pac).display !== "none" &&
            pac.childElementCount > 0;
          const hasHighlighted =
            pacOpen && pac.querySelector(".pac-item-selected");
          if (hasHighlighted) return;

          // Nessun suggerimento evidenziato: blocchiamo del tutto il listener
          // interno di Google (che con l'elenco aperto svuota il campo invece
          // di cercare, bug noto del widget Places) e avviamo la ricerca con
          // il testo attualmente digitato.
          e.preventDefault();
          e.stopImmediatePropagation();
          performAddressSearch();
        },
        true,
      );

      document.addEventListener("submit", (e) => {
        const form = e.target.closest('[data-role="address-form"]');
        if (!form) return;
        e.preventDefault();
        performAddressSearch();
      });

      // Filters form handling
      const filtersForm = document.getElementById("vc-stations-filters");
      if (filtersForm) {
        const serializeFilters = () => {
          const fd = new FormData(filtersForm);
          const params = {};

          for (const [k, v] of fd.entries()) {
            // Rimuove le parentesi quadre dal nome per il PHP (es: brand[] -> brand)
            const cleanKey = k.endsWith('[]') ? k.slice(0, -2) : k;

            if (k.endsWith('[]')) {
              // Se è un array, accumula i valori
              if (!params[cleanKey]) params[cleanKey] = [];
              params[cleanKey].push(v);
            } else {
              // Se è un campo singolo, salva il valore direttamente
              params[cleanKey] = v;
            }
          }
          return params;
        };

        filtersForm.addEventListener("submit", async (ev) => {
          ev.preventDefault();
          const params = serializeFilters();

          const qInput = document.getElementById("vc-stations-address-query");
          const qVal = qInput?.value?.trim();
          if (qVal) params.q = qVal;

          if (qVal) {
            // Come per il form indirizzo: se il testo non corrisponde a un
            // suggerimento selezionato, geocodifica lato client (il geocoding
            // server-side viene rifiutato da Google per via delle restrizioni
            // referer sulla chiave).
            const { loc, types } = await resolveQueryLocation(qVal);
            if (loc) {
              params.mode = "nearby";
              params.lat = loc.lat;
              params.lng = loc.lng;
              params.radius = types.includes("locality") ? 15 : 5;
              params.fallback = 1;
            } else {
              params.mode = "all";
              params.fallback = 1;
            }
          } else {
            // Nessuna posizione e nessuna query: stiamo filtrando solo per brand/tassonomie
            // Forziamo la ricerca globale con fallback per garantire risultati
            params.mode = "all";
            params.fallback = 1;
          }

          try {
            const data2 = await fetchStations(params);
            if (listWrapper) listWrapper.innerHTML = data2.html;
            const stations2 = collectStations(listWrapper);
            rebuildMarkersAndFit(stations2, data2.center || null);
            initAutocomplete();
          } catch (err) {
            console.warn("Errore durante il filtraggio", err);
          } finally {
            // chiusura modale robusta e indipendente da Bootstrap
            const modalEl = document.getElementById("trovastazioniFilters");
            if (modalEl) {
              let closed = false;
              try {
                if (window.bootstrap?.Modal) {
                  const inst =
                    window.bootstrap.Modal.getInstance(modalEl) ||
                    new window.bootstrap.Modal(modalEl);
                  inst.hide();
                  closed = true;
                }
              } catch (_) {
                /* ignore */
              }

              if (!closed) {
                // fallback 1: prova a cliccare il bottone close
                const closeBtn = modalEl.querySelector(
                  '.btn-close,[data-bs-dismiss="modal"]',
                );
                if (closeBtn) {
                  try {
                    closeBtn.click();
                    closed = true;
                  } catch (_) {
                    /* ignore */
                  }
                }
              }

              if (!closed) {
                // fallback 2: rimuovi classi/attributi e backdrop manualmente
                modalEl.classList.remove("show");
                modalEl.setAttribute("aria-hidden", "true");
                modalEl.style.display = "none";
                document.body.classList.remove("modal-open");
                document
                  .querySelectorAll(".modal-backdrop")
                  .forEach((b) => b.parentNode?.removeChild(b));
              }
            }
          }
        });

        filtersForm.addEventListener("keydown", (e) => {
          if (e.key === "Enter" && e.target.type === "checkbox") {
            e.preventDefault();
            filtersForm.requestSubmit();
          }
        });

        const resetBtn = filtersForm.querySelector(
          '[data-action="reset-filters"]',
        );
        if (resetBtn) {
          resetBtn.addEventListener("click", async () => {
            filtersForm
              .querySelectorAll('input[type="checkbox"]')
              .forEach((i) => {
                i.checked = false;
              });

            const params = {};
            const qInput = document.getElementById("vc-stations-address-query");
            const qVal = qInput?.value?.trim();
            if (qVal) params.q = qVal;

            if (qVal) {
              const { loc, types } = await resolveQueryLocation(qVal);
              if (loc) {
                params.mode = "nearby";
                params.lat = loc.lat;
                params.lng = loc.lng;
                params.radius = types.includes("locality") ? 15 : 5;
                params.fallback = 1;
              } else {
                params.mode = "all";
                params.fallback = 1;
              }
            }

            const data3 = await fetchStations(params);
            if (listWrapper) listWrapper.innerHTML = data3.html;
            const stations3 = collectStations(listWrapper);
            rebuildMarkersAndFit(stations3, data3.center || null);
            initAutocomplete();
          });
        }

        // Bootstrap da URL: applica filtri iniziali se presenti nella querystring
        (async () => {
          try {
            const usp = new URLSearchParams(window.location.search);
            const multiKeys = ["brand", "prodotti", "carte", "servizi", "adesioni"];
            let hasAnyFilter = false;

            // PRIORITARIO: se c'è un id in URL, forza fetch globale e apri
            const forcedId = usp.get('id');
            if (forcedId) {
              const paramsForced = { mode: 'all', fallback: 1 };
              const dataForced = await fetchStations(paramsForced);
              if (listWrapper) listWrapper.innerHTML = dataForced.html;
              const stationsForced = collectStations(listWrapper);
              rebuildMarkersAndFit(stationsForced, dataForced.center || null);

              const stForced = stationsForced.find((s) => String(s.id) === String(forcedId));
              if (stForced) {
                try {
                  highlightItem(stForced.el);
                  openStationDetail(stForced);
                  if (map && stForced.lat && stForced.lng) {
                    map.setCenter({ lat: stForced.lat, lng: stForced.lng });
                    map.setZoom(Math.max(map.getZoom(), 17));
                  }
                  scrollItemIntoView(stForced.el);
                } catch (e) {
                  console.warn('[TrovaStazioni] apertura dettaglio fallita (forced) per id', forcedId, e);
                }
              } else {
                console.warn('[TrovaStazioni] nessuna stazione trovata con id (forced)', forcedId);
              }
              // Il markup del form (con il campo indirizzo) è stato sostituito
              // da innerHTML: senza rilanciare initAutocomplete() il nuovo
              // input resta senza il widget Google Places collegato.
              initAutocomplete();
              // termina il bootstrap per evitare doppie fetch
              return;
            }

            // Preseleziona checkbox per i filtri multipli
            multiKeys.forEach((key) => {
              // Supporta sia ?key=a&key=b che ?key=a,b
              let vals = usp.getAll(key);
              if (vals.length === 0) {
                const v = usp.get(key);
                if (v) vals = v.split(",").map((s) => s.trim()).filter(Boolean);
              }
              if (vals.length) {
                hasAnyFilter = true;
                vals.forEach((val) => {
                  const input = filtersForm.querySelector(`input[name="${key}[]"][value="${CSS.escape(val)}"]`);
                  if (input) input.checked = true;
                });
              }
            });

            // Regione/Provincia passata da intro-regioni-province (non ha checkbox nel form)
            const urlRegione = usp.get("regione");
            if (urlRegione) hasAnyFilter = true;

            // Campo q opzionale
            const urlQ = usp.get("q");
            if (urlQ) {
              const qEl = document.getElementById("vc-stations-address-query");
              if (qEl) qEl.value = urlQ;
              hasAnyFilter = true;
            }

            // Costruisci params e invia richiesta se c'è almeno un filtro/url param
            if (hasAnyFilter) {
              const params = serializeFilters();
              if (urlQ) params.q = urlQ;
              if (urlRegione) params.regione = urlRegione;

              const urlLat = usp.get("lat");
              const urlLng = usp.get("lng");
              const urlRadius = usp.get("radius");

              if (urlLat && urlLng) {
                params.mode = "nearby";
                params.lat = parseFloat(urlLat);
                params.lng = parseFloat(urlLng);
                params.radius = urlRadius ? parseFloat(urlRadius) : (lastPlaceTypes.includes("locality") ? 15 : 5);
                params.fallback = 1;
              } else if (urlQ) {
                const { loc, types } = await resolveQueryLocation(urlQ);
                if (loc) {
                  params.mode = "nearby";
                  params.lat = loc.lat;
                  params.lng = loc.lng;
                  params.radius = types.includes("locality") ? 15 : 5;
                  params.fallback = 1;
                } else {
                  params.mode = "all";
                  params.fallback = 1;
                }
              } else if (!lastPlaceLoc) {
                params.mode = "all";
                params.fallback = 1;
              }

              const dataInit = await fetchStations(params);
              if (listWrapper) listWrapper.innerHTML = dataInit.html;
              const stationsInit = collectStations(listWrapper);
              rebuildMarkersAndFit(stationsInit, dataInit.center || null);

              // Se è presente un id in querystring, apri automaticamente la stazione corrispondente
              const targetId = usp.get("id");
              if (targetId) {
                const st = stationsInit.find((s) => String(s.id) === String(targetId));
                if (st) {
                  try {
                    // evidenzia e apri dettaglio
                    highlightItem(st.el);
                    openStationDetail(st);
                    // centra la mappa sulla stazione selezionata
                    if (map && st.lat && st.lng) {
                      map.setCenter({ lat: st.lat, lng: st.lng });
                      map.setZoom(Math.max(map.getZoom(), 17));
                    }
                    // scroll a vista
                    scrollItemIntoView(st.el);
                  } catch (e) {
                    console.warn('[TrovaStazioni] apertura dettaglio fallita per id', targetId, e);
                  }
                } else {
                  console.warn('[TrovaStazioni] nessuna stazione trovata con id', targetId);
                }
              }

              initAutocomplete();
            }
          } catch (e) {
            console.warn("Bootstrap filtri da URL fallito", e);
          }
        })();
      }
    });
  }

  /* -------------------------------------------------- */
  /* Station Detail Panel */
  let stationDetailTrigger = null;

  function closeStationDetail() {
    const panel = document.querySelector(ST_DETAIL_SEL);
    if (!panel) return;
    panel.hidden = true;
    panel.setAttribute("aria-hidden", "true");
    document.body.classList.remove("station-detail-open");
    if (stationDetailTrigger) {
      stationDetailTrigger.focus();
      stationDetailTrigger = null;
    }
  }

  function initStationDetailPanel() {
    const panel = document.querySelector(ST_DETAIL_SEL);
    if (!panel) return;

    const closeBtn = panel.querySelector(ST_CLOSE_BTN_SEL);
    closeBtn?.addEventListener("click", closeStationDetail);

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !panel.hidden) {
        closeStationDetail();
      }
    });
  }

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2)
      return decodeURIComponent(parts.pop().split(";").shift());
    return null;
  }

  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Raggio della Terra in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function openStationDetail(st) {
    const panel = document.querySelector(ST_DETAIL_SEL);
    if (!panel) {
      console.warn('[TrovaStazioni] Pannello dettaglio non trovato. Selettore:', ST_DETAIL_SEL);
      return;
    }

    const titleEl = panel.querySelector(ST_TITLE_SEL);
    const bodyEl = panel.querySelector(ST_BODY_SEL);

    // Recuperiamo i dati complessi passati da item.php
    let details = {};
    try {
      details = st.el.dataset.details ? JSON.parse(st.el.dataset.details) : {};
    } catch (e) {
      console.warn('[TrovaStazioni] Dettagli non parseabili per id', st?.id, e, st.el.dataset.details);
      details = {};
    }

    // 1. Titolo della modale
    if (titleEl) titleEl.textContent = st.title || "";

    let html = "";

    // 2. CONTENITORE GALLERY (ACF gallery -> prima immagine, fallback thumb di default per brand)
    const galleryUrl = details.gallery || details.default_thumb;
    if (galleryUrl) {
      html += `
      <div class="vc-station-detail__section vc-station-detail__section--gallery">
        <img src="${galleryUrl}" alt="${escapeHtml(st.title)}" class="w-100 h-100">
      </div>`;
    }

    // 3. CONTENITORE INFO (Titolo, Indirizzo, Tel, Maps + Distanza)

    // Recupero posizione dal cookie (assumendo che il cookie contenga un JSON tipo {lat: xx, lng: yy})
    const userLocCookie = getCookie("vcUserLoc"); // Cambia 'user_location' con il nome del tuo cookie
    let distanceHtml = "";

    if (userLocCookie) {
      try {
        const userPos = JSON.parse(userLocCookie);
        if (userPos.lat && userPos.lng && st.lat && st.lng) {
          const dist = calculateDistance(
            userPos.lat,
            userPos.lng,
            st.lat,
            st.lng,
          );
          // Formattiamo a 1 decimale (es. 1.2 km)
          distanceHtml = `<p class="station-distance">(dista ${dist.toFixed(1)} km da te)</p>`;
        }
      } catch (e) {
        console.error("Errore parsing cookie posizione", e);
      }
    }

    html += `
    <div class="vc-station-detail__section vc-station-detail__section--main">
      <div class="main-infos">
        <p class="station-title">${escapeHtml(st.title)}</p>
        <p class="station-address mb-1">${escapeHtml(st.address)}</p>
        ${distanceHtml}
      
        <div class="station-contacts mt-2">
          ${
            st.phone
              ? `
            <div class="phone">
              <a href="tel:${st.phone.replace(/\s+/g, "")}" class="btn-contact">
                Chiama
              </a>
            </div>`
              : ""
          }
            <div class="direction">
              <a href="https://www.google.com/maps/dir/?api=1&destination=${st.lat},${st.lng}" target="_blank" class="vega-btn btn-dark">
                <span>Ottieni indicazioni</span>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.36491 15.5359C7.22427 15.3952 7.14526 15.2045 7.14526 15.0056C7.14526 14.8067 7.22427 14.616 7.36491 14.4753L13.5086 8.33161L8.95572 8.33267C8.75658 8.33267 8.5656 8.25356 8.42478 8.11275C8.28397 7.97193 8.20486 7.78095 8.20486 7.58181C8.20486 7.38266 8.28397 7.19168 8.42478 7.05087C8.5656 6.91005 8.75658 6.83094 8.95572 6.83094L15.319 6.83094C15.4176 6.83082 15.5153 6.85015 15.6065 6.88784C15.6976 6.92554 15.7804 6.98084 15.8502 7.05059C15.9199 7.12034 15.9752 7.20316 16.0129 7.29432C16.0506 7.38547 16.07 7.48317 16.0698 7.58181L16.0698 13.9451C16.0698 14.1442 15.9907 14.3352 15.8499 14.476C15.7091 14.6168 15.5181 14.6959 15.319 14.6959C15.1198 14.6959 14.9288 14.6168 14.788 14.476C14.6472 14.3352 14.5681 14.1442 14.5681 13.9451L14.5692 9.39215L8.42545 15.5359C8.28481 15.6765 8.09407 15.7555 7.89518 15.7555C7.69629 15.7555 7.50555 15.6765 7.36491 15.5359Z" />
                </svg>
              </a>
            </div>
        </div>
      </div>
      <div class="brand">
        ${
          details.brand_logo
            ? `
          <img 
            src="${details.brand_logo}" 
            alt="${escapeHtml(details.brand)}" 
            class="station-brand-logo"
          >
        `
            : ""
        }
      </div>
    </div>
  `;

    // 4. CONTENITORE PRODOTTI E CARTE (SX: prodotti, DX: carte)
    html += `
    <div class="vc-station-detail__section vc-station-detail__section--extra">
      <div class="row">
        <div class="col-6">
          <h4 class="text-uppercase">Prodotti</h4>
          <ul class="list-unstyled">
            ${details.prodotti && details.prodotti.length ? details.prodotti.map((p) => `<li>${escapeHtml(p)}</li>`).join("") : "<li>-</li>"}
          </ul>
        </div>
        <div class="col-6 ps-3">
          <h4 class="text-uppercase">Carte</h4>
          <ul class="list-unstyled">
            ${details.carte && details.carte.length ? details.carte.map((c) => `<li>${escapeHtml(c)}</li>`).join("") : "<li>-</li>"}
          </ul>
        </div>
      </div>
    </div>
  `;

    // 5. CONTENITORE SERVIZI (Taxonomy "servizi" + ACF servizio_icon)
    if (details.servizi && details.servizi.length > 0) {
      html += `
      <div class="vc-station-detail__section vc-station-detail__section--services">
        <h4 class="text-uppercase">Servizi</h4>

        <div class="servizi">
        ${details.servizi
          .map(
            (s) => `
          <div class="servizio">
            ${s.icon ? `<img src="${s.icon}" class="icon" alt="icon">` : ""}
            <span class="">${escapeHtml(s.name)}</span>
          </div>
        `,
          )
          .join("")}
        </div>
      </div>
    `;
    }

    // --- 6. PANNELLO SPECIALE: RICARICA ELETTRICA ---
    const hasElectricService =
      details.servizi?.some((s) => s.slug === "ricarica-elettrica") || false;
    const hasColonnine = details.colonnine && details.colonnine.length > 0;

    if (hasElectricService || hasColonnine) {
      let colonnineHtml = "";

      if (hasColonnine) {
        colonnineHtml = details.colonnine
          .map(
            (col) => `
          
            <div class="vc-colonnina-item">
              <div class="info-colonnina">
              ${col.tipologia ? `<div class="tipologia">${escapeHtml(col.tipologia)}</div>` : ""}
              ${col.ricarica ? `<div class="ricarica">Ricarica ${escapeHtml(col.ricarica)}</div>` : ""}
              ${col.voltaggio ? `<div class="voltaggio">${escapeHtml(col.voltaggio)}</div>` : ""}
              </div>
              <img 
                src="/wp-content/themes/vegacarburanti/images/ico-ricarica-elettrica.png" 
                alt="Ricarica elettrica" 
                width="32" 
                height="32"
              >
            </div>
          
      `,
          )
          .join("");
      } else {
        // fallback se esiste solo il servizio ma non il repeater
        colonnineHtml = `
        <p class="default">
          Questa stazione dispone di postazioni per la ricarica di veicoli elettrici.
        </p>
      `;
      }

      html += `
      <div class="vc-station-detail__section vc-station-detail__section--colonnine">
        <div class="colonnine-title">
          <h4 class="text-uppercase">Colonnine di ricarica elettrica</h4>
          <img 
              src="/wp-content/themes/vegacarburanti/images/svg/logo-elektra.svg" 
              alt="Ricarica elettrica" 
              width="32" 
              height="32"
            >
          </div>
        <div class="vc-colonnine-wrapper">
          ${colonnineHtml}
        </div>
      </div>
    `;
    }

    // Funzione helper per mantenere solo <br>
    function keepOnlyBR(html) {
      return html ? html.replace(/<(?!br\s*\/?)[^>]+>/gi, "") : "";
    }

    // --- 7. PANNELLO SPECIALE: TO EAT ---
    const hasToEatService =
      details.servizi?.some((s) => s.slug === "bar-con-cucina-to-eat") || false;
    const hasToEatData =
      details.toeat &&
      (details.toeat.titolo ||
        details.toeat.indirizzo ||
        details.toeat.telefono ||
        details.toeat.orari);

    if (hasToEatService || hasToEatData) {
      let leftHtml = "";
      let rightHtml = "";

      if (hasToEatData) {
        // Colonna sinistra
        leftHtml = `
      ${
        details.toeat.titolo
          ? `
        <div class="toeat-name">
          <strong>${escapeHtml(details.toeat.titolo)}</strong>
        </div>
      `
          : ""
      }

      ${
        details.toeat.indirizzo
          ? `
        <div class="toeat-address">
          ${keepOnlyBR(details.toeat.indirizzo)}
        </div>
      `
          : ""
      }

      ${
        details.toeat.telefono?.title
          ? `
        <div class="toeat-phone">
          Tel. 
          <a class="underline-link" href="${details.toeat.telefono?.url || "#"}" target="${details.toeat.telefono?.target || "_self"}">
            ${escapeHtml(details.toeat.telefono.title)}
          </a>
        </div>
        <a href="https://toeatfood.it/ristoranti/" target="_blank" class="vega-btn btn-dark">
          <span>Visita il sito</span>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M7.36491 15.5359C7.22427 15.3952 7.14526 15.2045 7.14526 15.0056C7.14526 14.8067 7.22427 14.616 7.36491 14.4753L13.5086 8.33161L8.95572 8.33267C8.75658 8.33267 8.5656 8.25356 8.42478 8.11275C8.28397 7.97193 8.20486 7.78095 8.20486 7.58181C8.20486 7.38266 8.28397 7.19168 8.42478 7.05087C8.5656 6.91005 8.75658 6.83094 8.95572 6.83094L15.319 6.83094C15.4176 6.83082 15.5153 6.85015 15.6065 6.88784C15.6976 6.92554 15.7804 6.98084 15.8502 7.05059C15.9199 7.12034 15.9752 7.20316 16.0129 7.29432C16.0506 7.38547 16.07 7.48317 16.0698 7.58181L16.0698 13.9451C16.0698 14.1442 15.9907 14.3352 15.8499 14.476C15.7091 14.6168 15.5181 14.6959 15.319 14.6959C15.1198 14.6959 14.9288 14.6168 14.788 14.476C14.6472 14.3352 14.5681 14.1442 14.5681 13.9451L14.5692 9.39215L8.42545 15.5359C8.28481 15.6765 8.09407 15.7555 7.89518 15.7555C7.69629 15.7555 7.50555 15.6765 7.36491 15.5359Z" />
          </svg>
        </a>
      `
          : ""
      }`;

        // Colonna destra
        rightHtml = details.toeat.orari
          ? `
      <div class="toeat-hours">
        <span>Orari:</span><br/>
        ${keepOnlyBR(details.toeat.orari)}
      </div>
    `
          : "";
      } else {
        // fallback generico
        leftHtml = `<p class="default">Presso questa stazione è disponibile il servizio ristorazione ToEAT.</p>`;
      }

      html += `
    <div class="vc-station-detail__section vc-station-detail__section--toeat">
      <div class="toeat-title">
        <h4 class="text-uppercase">BAR CON CUCINA E PIZZERIA</h4>
        <img src="/wp-content/themes/vegacarburanti/images/svg/logo-toEAT.svg" alt="ToEAT">
      </div>

      <div class="vc-toeat-wrapper row">
        <div class="col-6 vc-toeat-left">
          ${leftHtml}
        </div>
        <div class="col-6 vc-toeat-right">
          ${rightHtml}
        </div>
      </div>
    </div>
  `;
    }

    bodyEl.innerHTML = html;

    // Apertura modale
    panel.hidden = false;
    panel.setAttribute("aria-hidden", "false");
    document.body.classList.add("station-detail-open");

    // Gestione focus da tastiera
    stationDetailTrigger = document.activeElement;
    const closeBtn = panel.querySelector(ST_CLOSE_BTN_SEL);
    if (closeBtn) closeBtn.focus();
  }

  /* -------------------------------------------------- */
  function highlightItem(el) {
    document
      .querySelectorAll(".vc-station-item--active")
      .forEach((n) => n.classList.remove("vc-station-item--active"));
    el.classList.add("vc-station-item--active");
  }

  function scrollItemIntoView(el) {
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", init)
    : init();
})();
